import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import _ from 'lodash';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { handleApiRequest } from '../../api';

import useApiError from '../../hook/useApiError';
import useUserAuth from '../../hook/useUserAuth';
import useGlobalStatus from '../../hook/useGlobalStatus';

import { formatDate2 } from '../../utils/date';

import { REPORT_TYPE } from './constants';
import { CardStyle, ColorSpan } from './frontPageStyle';


const Summary = ({ userQuery, dateRange, summaryData, setSummaryData }) => {
  const { handleApiErrorStatus } = useApiError();
  const { drawerOpen } = useGlobalStatus();

  const filterCondition = new URLSearchParams({
    start_date: dateRange.startDate ? formatDate2(dateRange.startDate) : '',
    end_date: dateRange.endDate ? formatDate2(dateRange.endDate) : '',
    min_user_level: userQuery[0] / 100,
    max_user_level: userQuery[1] / 100,
  })

  const getTotalUsers = () => {
    setSummaryData({});
    handleApiRequest('getTotalUsers', filterCondition)
      .then(res => {
        if (res.status === 200) {
          setSummaryData(prev => ({
            ...prev,
            [REPORT_TYPE.total_users.id]: res.data.result
          }));
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  const getTotalPurchaseUrls = () => {
    setSummaryData({});
    handleApiRequest('getTotalPurchaseUrls', filterCondition)
      .then(res => {
        if (res.status === 200) {
          setSummaryData(prev => ({
            ...prev,
            [REPORT_TYPE.total_purchase_urls.id]: res.data.result
          }));
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  const getTotalUserQuestions = () => {
    setSummaryData({});
    handleApiRequest('getTotalUserQuestions', filterCondition)
      .then(res => {
        if (res.status === 200) {
          setSummaryData(prev => ({
            ...prev,
            [REPORT_TYPE.total_user_questions.id]: res.data.result
          }));
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  const getHourlyUserQuestions = () => {
    setSummaryData({});
    handleApiRequest('getHourlyUserQuestions', filterCondition)
      .then(res => {
        if (res.status === 200) {
          setSummaryData(prev => ({
            ...prev,
            [REPORT_TYPE.hourly_user_questions.id]: res.data.result
          }));
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  const getTodayYesterdayUserQuestions = () => {
    setSummaryData({});
    const params = new URLSearchParams({
      min_user_level: userQuery[0] / 100,
      max_user_level: userQuery[1] / 100,
    });
    handleApiRequest('getTodayYesterdayUserQuestions', params)
      .then(res => {
        if (res.status === 200) {
          setSummaryData(prev => ({
            ...prev,
            [REPORT_TYPE.day_questions.id]: res.data.result
          }));
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  const getLastMonthThisMonthUserQuestions = () => {
    setSummaryData({});
    const params = new URLSearchParams({
      min_user_level: userQuery[0] / 100,
      max_user_level: userQuery[1] / 100,
    });
    handleApiRequest('getLastMonthThisMonthUserQuestions', params)
      .then(res => {
        if (res.status === 200) {
          setSummaryData(prev => ({
            ...prev,
            [REPORT_TYPE.month_questions.id]: res.data.result
          }));
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  useEffect(() => {
    getTotalUsers();
    getTotalPurchaseUrls();
    getTotalUserQuestions();
    getHourlyUserQuestions();
    getTodayYesterdayUserQuestions();
    getLastMonthThisMonthUserQuestions();
  }, [userQuery, dateRange])

  const checkRiseOrFall = (rate) => {
    const rateValue = parseFloat(rate);

    if (!isNaN(rateValue) && rateValue > 0) {
      return "+";
    } else {
      return "";
    }
  }

  const checkRateColor = (rate) => {
    const rateValue = parseFloat(rate);

    if (!isNaN(rateValue) && rateValue > 0) {
      return "red";
    } else {
      return "green";
    }
  }

  return (
    <Box>
      <CardStyle>
        <Typography variant='h5' mb={1}>{REPORT_TYPE.total_users.name}</Typography>
        <Typography variant='body_lg'>{summaryData.total_users || '--'}</Typography>
      </CardStyle>

      <CardStyle>
        <Typography variant='h5' mb={1}>{REPORT_TYPE.total_purchase_urls.name}</Typography>
        <Typography variant='body_lg'>{summaryData.total_purchase_urls || '--'}</Typography>
      </CardStyle>

      <CardStyle>
        <Typography variant='h5' mb={1}>{REPORT_TYPE.total_user_questions.name}</Typography>
        <Typography variant='body_lg'>{summaryData.total_user_questions || '--'}</Typography>
      </CardStyle>

      <CardStyle>
        <Typography variant='h5' mb={1}>{REPORT_TYPE.hourly_user_questions.name}</Typography>
        <Typography variant='body_lg'>{summaryData.hourly_user_questions || '--'}</Typography>
      </CardStyle>

      <CardStyle>
        <Typography variant='h5' mb={1}>{REPORT_TYPE.day_questions.name}</Typography>
        <Typography variant='body_lg'>
          <ColorSpan color={checkRateColor(summaryData.day_questions?.change_rate)}>{summaryData.day_questions?.after || '--'}</ColorSpan>
        </Typography>
        <Typography variant='h5'>
          昨天：{summaryData.day_questions?.before || '--'} <ColorSpan color={checkRateColor(summaryData.day_questions?.change_rate)}>({checkRiseOrFall(summaryData.day_questions?.change_rate)} {summaryData.day_questions?.change_rate || '0%'})</ColorSpan>
        </Typography>
      </CardStyle>

      <CardStyle>
        <Typography variant='h5' mb={1}>{REPORT_TYPE.month_questions.name}</Typography>
        <Typography variant='body_lg'>
          <ColorSpan color={checkRateColor(summaryData.month_questions?.change_rate)}>{summaryData.month_questions?.after || '--'}</ColorSpan>
        </Typography>
        <Typography variant='h5'>上月：
          {summaryData.month_questions?.before || '--'} <ColorSpan color={checkRateColor(summaryData.month_questions?.change_rate)}>({checkRiseOrFall(summaryData.month_questions?.change_rate)}{summaryData.month_questions?.change_rate || '0%'})</ColorSpan>
        </Typography>
      </CardStyle>
    </Box>
  )
}

export default Summary;